import './item-card.scss';

import { PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '../../atoms/skeleton/skeleton';
import { cn } from '../../utils/helpers';

export type ItemCardProps = PropsWithChildren & {
  className?: string;
  color?: 'negative' | 'warning';
};

/**
 * Item Card
 */
export const ItemCard: React.FC<ItemCardProps> = ({
  color,
  className,
  children,
}) => {
  const colorClass = color
    ? {
        warning: 'ItemCard--warning',
        negative: 'ItemCard--negative',
      }[color]
    : '';

  return (
    <div className={cn(['ItemCard rounded-md', colorClass, className])}>
      {children}
    </div>
  );
};

export const ItemCardSection: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="ItemCard__section py-comfy-sm md:py-comfy-lg pb-comfy-xs px-comfy-sm md:pb-comfy-md md:px-comfy-lg">
    {children}
  </div>
);

/**
 * Item Card Header
 */
export const ItemCardHeader: React.FC<{
  title?: string;
  className?: string;
  badge?: ReactNode;
}> = ({ title, className = '', badge }) => (
  <div
    className={cn(
      ['ItemCard__header flex justify-between items-center', className],
      {
        'mb-comfy-sm md:mb-comfy-md': !badge,
        'mb-comfy-xs md:mb-comfy-sm': !!badge,
      },
    )}
  >
    {title && (
      <h3 className="label-neutral-amplified label-potent md:label-potent label-md md:label-lg">
        {title}
      </h3>
    )}
    {badge}
  </div>
);

/**
 * Item Card Body - if you need full control
 */
export const ItemCardBody: React.FC<PropsWithChildren> = ({ children }) => (
  <div className="ItemCard__body grid gap-snug-xl md:gap-comfy-sm items-center">
    {children}
  </div>
);

export const ItemCardSummary: React.FC<{
  icon?: ReactNode;
  title?: string;
  subTitle?: string;
  isWarning?: boolean;
  isError?: boolean;
  isSubtleTitle?: boolean;
}> = ({
  icon,
  title,
  subTitle,
  isWarning = false,
  isError = false,
  isSubtleTitle = false,
}) => {
  if (!title && !subTitle) {
    return;
  }

  return (
    <>
      {icon}
      {(title || subTitle) && (
        <div className="ItemCard__titlegroup">
          {title && (
            <div
              className={cn('ItemCard__title label-md md:label-lg mb-snug-md', {
                'label-potent md:label-potent label-neutral-amplified':
                  !isSubtleTitle,
                'label-neutral-potent': isSubtleTitle,
              })}
            >
              {title}
            </div>
          )}

          {subTitle && (
            <div
              className={cn(`label-neutral-mild label-sm md:label-md`, {
                'label-warning-moderate': isWarning,
                'label-negative-moderate': isError,
              })}
            >
              {subTitle}
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Item Card Meta
 *
 * NOTE Organizes right-aligned text with an optional badge.
 */
export const ItemCardMeta: React.FC<{
  text?: string;
  textContrast?: string;
  badge?: ReactNode;
}> = ({ text, textContrast, badge }) => (
  <div className="flex flex-col gap-snug-md md:gap-snug-xl items-end text-right">
    <span className="label-md md:label-lg">
      <span className="label-neutral-amplified">{text}</span>
      {textContrast && (
        <span className="label-neutral-mild">{textContrast}</span>
      )}
    </span>
    {badge}
  </div>
);

export const ItemCardDivider = () => <div className="ItemCard__divider" />;

/**
 * Item Card Footer
 *
 * NOTE that this should almost always take in one or more client components.
 */
export const ItemCardFooter: React.FC<
  PropsWithChildren<{ justify?: 'end' | 'between' }>
> = ({ children, justify = 'between' }) => (
  <ItemCardSection>
    <div
      className={cn([
        'ItemCard__footer flex',
        {
          'justify-between': justify === 'between',
          'justify-end': justify === 'end',
        },
      ])}
    >
      {children}
    </div>
  </ItemCardSection>
);

export const ItemCardSkeleton = () => <Skeleton height={180} />;
